import { IconCheck, IconFilter, IconX } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'shared/Button';
import { Checkbox, TextInput } from 'shared/Input';
import { DEFAULT_FILTER_BY, FILTER_BY_STATUSES_OPTIONS } from '../constants';
import { useSiteSelectorContext } from '../SiteSelectorContext';
import { FilterBy } from '../types';
import { PopoverForm } from './PopoverForm';

const hasActiveFilters = (filterBy: FilterBy) =>
    Object.values(filterBy).some(
        /*
         * `length` is a property of both `string` and `Array` so leverage this
         * fact to check `search` and `statuses` filters in a single condition.
         */
        (filterValue) => filterValue.length > 0
    );

export const FilterByPopover = () => {
    const { t } = useTranslation('navigation', {
        keyPrefix: 'site_selector',
    });
    const { t: tSites } = useTranslation('sites');

    const { filterBy, setFilterBy } = useSiteSelectorContext();

    const [filterByLocal, setFilterByLocal] = useState(filterBy);

    const onCloseFilterBy = useCallback(() => {
        setFilterByLocal(filterBy);
    }, [filterBy]);

    return (
        <PopoverForm
            dirty={hasActiveFilters(filterBy)}
            icon={IconFilter}
            label={t('controls.filter_by.label')}
            onClose={onCloseFilterBy}
            onReset={(event) => {
                event.preventDefault();
                setFilterByLocal(DEFAULT_FILTER_BY);
            }}
            onSubmit={(event) => {
                event.preventDefault();
                setFilterBy(filterByLocal);
            }}
        >
            <TextInput
                aria-label={t('controls.filter_by.search.label')}
                autoComplete="off"
                className="[&_input]:-mt-2 [&_input]:px-4 [&_input]:py-2"
                id="filterBySearch"
                name="filterBySearch"
                placeholder={t('controls.filter_by.search.placeholder')}
                value={filterByLocal.search ?? ''}
                onChange={(value) =>
                    setFilterByLocal((currentFilterByLocal) => ({
                        ...currentFilterByLocal,
                        search: value,
                    }))
                }
            />

            <h3 className="font-semibold mb-5 mt-6 text-base">
                {t('controls.filter_by.statuses.heading')}
            </h3>

            {FILTER_BY_STATUSES_OPTIONS.map((filterByStatusesOption) => (
                <div key={filterByStatusesOption} className="lowercase mb-2">
                    <Checkbox
                        id={`filterByStatuses-${filterByStatusesOption}`}
                        label={tSites(`status.site.${filterByStatusesOption}`)}
                        name={`filterByStatuses-${filterByStatusesOption}`}
                        value={
                            !!filterByLocal.statuses?.includes(
                                filterByStatusesOption
                            )
                        }
                        onChange={(value) =>
                            setFilterByLocal((currentFilterByLocal) => ({
                                ...currentFilterByLocal,
                                statuses: value
                                    ? [
                                          ...(currentFilterByLocal.statuses ??
                                              []),
                                          filterByStatusesOption,
                                      ]
                                    : currentFilterByLocal.statuses?.filter(
                                          (status) =>
                                              status !== filterByStatusesOption
                                      ),
                            }))
                        }
                    />
                </div>
            ))}

            <div className="flex justify-between mt-4">
                {hasActiveFilters(filterByLocal) ? (
                    <Button
                        className="bg-transparent !text-primary hover:bg-white/30"
                        color="gray"
                        outline
                        size="xs"
                        type="reset"
                    >
                        {t('controls.filter_by.reset')}
                        <IconX />
                    </Button>
                ) : null}

                <Button className="ml-auto" outline size="xs" type="submit">
                    {t('controls.cta.apply')}
                    <IconCheck />
                </Button>
            </div>
        </PopoverForm>
    );
};
